import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import { CalendarDays, Clock, LogIn, LogOut, RefreshCw } from "lucide-react";

const STATUS_COLORS = {
  Present: "bg-emerald-100 text-emerald-700 border-emerald-200",
  WFH: "bg-sky-100 text-sky-700 border-sky-200",
  "Half-day": "bg-amber-100 text-amber-700 border-amber-200",
  Absent: "bg-rose-100 text-rose-700 border-rose-200",
  Leave: "bg-violet-100 text-violet-700 border-violet-200",
  Holiday: "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Weekly Off": "bg-slate-100 text-slate-600 border-slate-200",
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function MyAttendance() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [today, setToday] = useState(null);
  const [punchStatus, setPunchStatus] = useState("Present");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y, y - 1, y - 2, y - 3];
  }, [now]);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [hist, td] = await Promise.all([
        api.get("/attendance", { params: { month, year } }),
        api.get("/attendance/self/today").catch(() => ({ data: null })),
      ]);
      setRows(hist.data || []);
      setToday(td.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to load attendance");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const punch = async (kind) => {
    setBusy(true);
    setErr("");
    try {
      if (kind === "in") {
        await api.post("/attendance/self/clock-in", { status: punchStatus });
      } else {
        await api.post("/attendance/self/clock-out");
      }
      await load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Punch failed");
    } finally {
      setBusy(false);
    }
  };

  const counts = useMemo(() => {
    const c = { Present: 0, WFH: 0, Leave: 0, Absent: 0, Other: 0 };
    for (const r of rows) {
      if (c[r.status] != null) c[r.status] += 1;
      else c.Other += 1;
    }
    return c;
  }, [rows]);

  const rec = today?.record;

  return (
    <AdminLayout title="My Attendance" breadcrumbs={["Home", "Self Service", "Attendance"]}>
      {err && (
        <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
          {err}
        </div>
      )}

      {/* Today punch card */}
      <div className="bg-white border border-slate-200 rounded shadow-sm p-5 mb-5" data-testid="self-attendance-today">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#226DB4]" /> Today — {formatDisplayDate(today?.date)}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Record your own attendance (clock in / out). History below supports previous months.
            </p>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-slate-500">Status:</span>{" "}
                <span className="font-semibold text-slate-800">{rec?.status || "Not marked"}</span>
              </div>
              <div>
                <span className="text-slate-500">In:</span>{" "}
                <span className="font-semibold text-slate-800">{rec?.check_in || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500">Out:</span>{" "}
                <span className="font-semibold text-slate-800">{rec?.check_out || "—"}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-[11px] uppercase text-slate-500 mb-1">Work mode</label>
              <select
                value={punchStatus}
                onChange={(e) => setPunchStatus(e.target.value)}
                disabled={!today?.can_clock_in}
                className="border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="Present">Office / Present</option>
                <option value="WFH">Work From Home</option>
              </select>
            </div>
            <button
              type="button"
              disabled={busy || !today?.can_clock_in}
              onClick={() => punch("in")}
              data-testid="clock-in-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-50"
            >
              <LogIn className="h-4 w-4" /> Clock In
            </button>
            <button
              type="button"
              disabled={busy || !today?.can_clock_out}
              onClick={() => punch("out")}
              data-testid="clock-out-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium disabled:opacity-50"
            >
              <LogOut className="h-4 w-4" /> Clock Out
            </button>
            <button
              type="button"
              onClick={load}
              className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Month filter + summary */}
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[#226DB4]" /> Attendance history
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
              data-testid="attendance-month"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
              data-testid="attendance-year"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-5 gap-3 border-b border-slate-100 text-sm">
          <div><span className="text-slate-500">Present</span> <b className="ml-1">{counts.Present}</b></div>
          <div><span className="text-slate-500">WFH</span> <b className="ml-1">{counts.WFH}</b></div>
          <div><span className="text-slate-500">Leave</span> <b className="ml-1">{counts.Leave}</b></div>
          <div><span className="text-slate-500">Absent</span> <b className="ml-1">{counts.Absent}</b></div>
          <div><span className="text-slate-500">Other</span> <b className="ml-1">{counts.Other}</b></div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Check-in</th>
                <th className="px-4 py-2 text-left">Check-out</th>
                <th className="px-4 py-2 text-left">Note</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No records for this month.</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id || r.date} className="border-t border-slate-100">
                    <td className="px-4 py-2 whitespace-nowrap">{formatDisplayDate(r.date)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 text-xs rounded border ${STATUS_COLORS[r.status] || "bg-slate-100"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">{r.check_in || "—"}</td>
                    <td className="px-4 py-2">{r.check_out || "—"}</td>
                    <td className="px-4 py-2 text-slate-500">{r.note || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
